import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  FileSignature,
  ShieldCheck,
  Search,
  BookOpen,
  Eye,
  Sparkles,
  X,
  Lock,
  CreditCard,
} from "lucide-react";
import {
  CONTRACT_TEMPLATES_META,
  type ContractMeta,
} from "@/lib/contract-templates";
import { Disclaimer } from "@/components/site/Disclaimer";
import { TemplatePreview } from "@/components/site/TemplatePreview";
import { QuickDownloadButton } from "@/components/site/QuickDownloadButton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";


export const Route = createFileRoute("/contracts")({
  head: () => ({
    meta: [
      {
        title: "Free SA Contract, CV & Cover Letter Templates",
      },
      {
        name: "description",
        content:
          "Branded, ready-made South African contracts, CVs and cover letters as PDF — every template is free to personalise and download.",
      },
      {
        property: "og:title",
        content: "Free SA Document Templates (PDF)",
      },
      {
        property: "og:description",
        content:
          "Contracts, CVs and cover letters drafted by Nkadimeng Danny Legal & Compliance Support — every template free to personalise and download.",
      },
    ],
  }),
  component: ContractsPage,
});

const TEMPLATES = CONTRACT_TEMPLATES_META;
const CATEGORIES = ["All", ...Array.from(new Set(TEMPLATES.map((t) => t.category))).sort()];

function ContractsPage() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("All");
  const [preview, setPreview] = useState<ContractMeta | null>(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return TEMPLATES.filter((t) => {
      if (cat !== "All" && t.category !== cat) return false;
      if (!needle) return true;
      return (
        t.title.toLowerCase().includes(needle) ||
        t.description.toLowerCase().includes(needle) ||
        t.category.toLowerCase().includes(needle)
      );
    });
  }, [q, cat]);

  return (
    <div className="bg-background">
      <section className="relative overflow-hidden border-b border-border bg-navy text-navy-foreground">
        <div aria-hidden className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div aria-hidden className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gold/20 blur-3xl" />
        <div aria-hidden className="absolute -bottom-40 -left-20 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />

        <div className="container-narrow relative py-14 md:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-gold">
            <Sparkles className="h-3 w-3" /> Document library
          </div>
          <h1 className="mt-4 font-display text-4xl leading-tight md:text-6xl">
            South African contracts,
            <br className="hidden sm:block" />
            <span className="text-gold">CVs &amp; legal templates.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/80 md:text-lg">
            Branded, statute-cited PDFs — generated on the fly with our letterhead and
            POPIA-aligned footer. Every template is free to personalise and download instantly.
          </p>

          <div className="mt-8 grid max-w-md grid-cols-2 gap-3 text-center sm:gap-6">
            <Stat n={TEMPLATES.length} label="Templates" />
            <Stat n={TEMPLATES.length} label="Free" />
          </div>

          <div className="mt-8 rounded-2xl border border-white/15 bg-white/95 p-3 shadow-2xl backdrop-blur md:p-4">
            <div className="flex items-center gap-2">
              <Search className="ml-1 h-5 w-5 shrink-0 text-navy/70" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for NDA, lease, demand, CV, payment plan…"
                className="min-w-0 flex-1 bg-transparent text-sm text-navy placeholder:text-navy/40 focus:outline-none md:text-base"
                aria-label="Search templates"
              />
              {q && (
                <button type="button" onClick={() => setQ("")} className="rounded-full p-1 text-navy/60 hover:bg-navy/10 hover:text-navy" aria-label="Clear search">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="mt-5 max-w-3xl">
            <Disclaimer variant="compact" />
          </div>
        </div>
      </section>

      <section className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container-narrow flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between">
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0 md:pb-0">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={
                  "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition " +
                  (cat === c
                    ? "border-navy bg-navy text-navy-foreground shadow-soft"
                    : "border-border bg-card text-navy hover:border-navy/40 hover:bg-secondary")
                }
              >
                {c}
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground">
            {filtered.length} of {TEMPLATES.length}
          </span>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container-narrow">
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
              <p className="font-display text-lg text-navy">No templates match that filter.</p>
              <button
                type="button"
                onClick={() => { setQ(""); setCat("All"); }}
                className="mt-4 inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-navy hover:bg-secondary"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((t) => (
                <TemplateCard key={t.slug} meta={t} onPreview={() => setPreview(t)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-gradient-to-br from-navy via-navy to-navy/90 py-14 text-navy-foreground">
        <div className="container-narrow grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gold/20 text-gold">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl">
                Need a contract tailored to your facts?
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-white/75 md:text-base">
                Book a consultation and we&apos;ll draft, review or negotiate the
                terms with you — and refer to an admitted attorney where court
                representation is required.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/book" className="inline-flex items-center justify-center rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-navy hover:bg-gold/90">
              Book a consultation
            </Link>
            <Link to="/legislation" className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-transparent px-5 py-2.5 text-sm font-medium text-white hover:bg-white/10">
              <BookOpen className="h-4 w-4" /> Browse legislation
            </Link>
          </div>
        </div>
      </section>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0">
          {preview && (
            <div className="grid md:grid-cols-[1fr_1.1fr]">
              <div className="bg-gradient-to-br from-navy/5 via-background to-gold/10 p-6">
                <TemplatePreview meta={preview} className="mx-auto w-full max-w-[280px]" />
              </div>
              <div className="flex flex-col p-6 md:p-7">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
                  {preview.category}
                  <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] tracking-normal text-white">
                    Free
                  </span>
                </div>
                <DialogTitle className="mt-2 font-display text-2xl leading-snug text-navy">
                  {preview.title}
                </DialogTitle>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {preview.description}
                </p>
                <ul className="mt-5 space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />Branded PDF with logo, contact details and POPIA footer</li>
                  <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />Statute-cited clauses (OSCOLA references in footer)</li>
                  <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />Personalise &amp; download instantly — no signup required</li>
                </ul>
                <div className="mt-auto flex flex-col gap-2 pt-6 sm:flex-row">
                  <Link
                    to="/contracts/$slug"
                    params={{ slug: preview.slug }}
                    onClick={() => setPreview(null)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-navy px-4 py-2.5 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
                  >
                    <FileSignature className="h-4 w-4" /> Personalise &amp; download
                  </Link>
                  <QuickDownloadButton
                    href={`/api/public/contract?slug=${preview.slug}`}
                    filename={`${preview.slug}.pdf`}
                    label="Download blank PDF"
                    variant="full"
                    className="px-4 py-2.5 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="rounded-xl border border-white/15 bg-white/5 px-3 py-3 backdrop-blur">
      <div className="font-display text-2xl text-gold md:text-3xl">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 md:text-xs">
        {label}
      </div>
    </div>
  );
}

function TemplateCard({ meta, onPreview }: { meta: ContractMeta; onPreview: () => void }) {
  const premium = !!meta.premium;
  const price = meta.priceZar ?? 0;
  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition hover:-translate-y-1 hover:border-navy/30 hover:shadow-lifted">
      <button
        type="button"
        onClick={onPreview}
        aria-label={`Preview ${meta.title}`}
        className="relative block w-full overflow-hidden bg-gradient-to-br from-navy/5 via-background to-gold/10 p-5 text-left"
      >
        <TemplatePreview meta={meta} className="mx-auto w-full max-w-[180px] transition group-hover:scale-[1.03]" />

        <div className="absolute left-3 top-3">
          <span className="rounded-full bg-card/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-navy shadow-soft">
            {meta.category}
          </span>
        </div>
        <div className="absolute right-3 top-3">
          {premium ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold text-navy shadow-soft">
              <Lock className="h-3 w-3" /> R{price}
            </span>
          ) : (
            <span className="rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-bold text-white shadow-soft">
              FREE
            </span>
          )}
        </div>

        <div
          className="absolute bottom-3 right-3"
          onClick={(e) => e.stopPropagation()}
        >
          {premium ? (
            <Link
              to="/contracts/$slug"
              params={{ slug: meta.slug }}
              className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-[11px] font-semibold text-navy-foreground shadow-lifted hover:bg-navy/90"
            >
              <CreditCard className="h-3 w-3" /> Buy R{price}
            </Link>
          ) : (
            <QuickDownloadButton
              href={`/api/public/contract?slug=${meta.slug}`}
              filename={`${meta.slug}.pdf`}
              label="Quick download"
              variant="full"
              className="bg-white/95 px-2.5 py-1.5 text-[11px] shadow-lifted backdrop-blur"
            />
          )}
        </div>

        <div className="pointer-events-none absolute inset-0 flex items-end justify-center bg-gradient-to-t from-navy/85 via-navy/30 to-transparent p-4 opacity-0 transition group-hover:opacity-100">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-navy shadow-lifted">
            <Eye className="h-3.5 w-3.5" /> {premium ? "Preview watermarked sample" : "Quick preview"}
          </span>
        </div>
      </button>

      <div className="flex flex-1 flex-col p-5 pt-4">
        <h2 className="font-display text-base leading-snug text-navy">{meta.title}</h2>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">
          {meta.description}
        </p>
        {premium && (
          <p className="mt-2 text-[11px] font-medium text-amber-800">
            Premium template · Pay R{price} via PayFast to unlock the full personalised PDF.
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Link
            to="/contracts/$slug"
            params={{ slug: meta.slug }}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-navy px-3 py-2 text-sm font-semibold text-navy-foreground shadow-soft hover:bg-navy/90"
          >
            {premium ? (
              <>
                <CreditCard className="h-3.5 w-3.5" /> Pay R{price} & download
              </>
            ) : (
              <>
                <FileSignature className="h-3.5 w-3.5" /> Personalise
              </>
            )}
          </Link>
          {premium ? (
            <QuickDownloadButton
              href={`/api/public/contract?slug=${meta.slug}&sample=1`}
              filename={`${meta.slug}-sample-preview.pdf`}
              label="Sample (watermarked)"
              variant="icon"
            />
          ) : (
            <QuickDownloadButton
              href={`/api/public/contract?slug=${meta.slug}`}
              filename={`${meta.slug}.pdf`}
              label="Download blank PDF"
              variant="icon"
            />
          )}
          <button
            type="button"
            onClick={onPreview}
            title="Preview"
            aria-label="Preview template"
            className="inline-flex items-center justify-center gap-1 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-navy hover:border-navy/40 hover:bg-secondary"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

